import { Formik } from 'formik';
import { memo, useCallback, useEffect, useState } from 'react';
import * as Yup from 'yup';

import { useTrackClickedExportAsButton } from '@air/analytics';
import { useSavedSizesCatalog } from '@air/feature-saved-sizes/use-saved-sizes-catalog';
import { getSavedSizeSelectionId, type SavedSize } from '@air/feature-saved-sizes/utils';
import { Button } from '@air/primitive-button';
import { Modal, ModalCloseButton, ModalTitle } from '@air/primitive-modal';
import { reportErrorToBugsnag } from '@air/utils-error';
import { cn } from '@air/utils-tailwind';

import { stopEscKeyPropagationOnAssetDetailsModal } from '~/components/AssetModalV2/hooks/useAssetModalEscKeyHandler';
import { ExportImagesModalForm } from '~/components/ExportImagesModal/components/ExportImagesModalForm/ExportImagesModalForm';
import Form from '~/components/Form';
import { defaultImageSizeOptions } from '~/components/ImageFormatFormField';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { DownloadFile } from '~/utils/DownloadFile';
import { createImgixImageUrl, type ImgixSupportedExportFormat } from '~/utils/Imgix';

const EXPORT_IMAGES_MODAL_FORM_ID = 'exportImagesModalFormId';
const EXPORT_LOADING_DELAY_MS = 1000;

const extractFileName = (format: string, baseName: string, suffix?: string) => {
  return suffix ? `${baseName}${suffix}.${format}` : `${baseName}.${format}`;
};

export type ExportImagesFormData = {
  width: number;
  height: number;
  imageSrc: string;
  size: string;
  fileName: string;
  suffix?: string;
  ext: ImgixSupportedExportFormat;
};

// A saved size's own name is user-authored text and must never flow into an analytics param value.
const SAVED_SIZE_ANALYTICS_LABEL = 'Saved size';
// Stable identity so ExportImageSizeSelect's memoization doesn't see a new array on every render.
const NO_SAVED_SIZES: SavedSize[] = [];
const MAX_EXPORT_DIMENSION = 16000;
const IMAGE_SIZE_ERROR = `Asset size must be between 1px and ${MAX_EXPORT_DIMENSION}px`;
const validationSchema = Yup.object().shape({
  ext: Yup.string().required(),
  height: Yup.number().min(1, IMAGE_SIZE_ERROR).max(MAX_EXPORT_DIMENSION, IMAGE_SIZE_ERROR).required(IMAGE_SIZE_ERROR),
  size: Yup.string().required(),
  suffix: Yup.string().notRequired(),
  width: Yup.number().min(1, IMAGE_SIZE_ERROR).max(MAX_EXPORT_DIMENSION, IMAGE_SIZE_ERROR).required(IMAGE_SIZE_ERROR),
});

export type ExportImagesModalProps = {
  /** Runs the single-image canvas crop handoff. This modal closes after it. Omit when the surface cannot open canvas. */
  onCropAndEditOnCanvas?: () => void;
  /** Omit when mounting from a public/share-link surface with no workspace context. */
  workspaceId?: string;
} & AirModalProps &
  Pick<ExportImagesFormData, 'width' | 'height' | 'imageSrc' | 'fileName'>;

export const ExportImagesModal = memo(
  ({ width, height, imageSrc, fileName, onClose, onCropAndEditOnCanvas, workspaceId }: ExportImagesModalProps) => {
    const { trackClickedExportAsButton } = useTrackClickedExportAsButton();
    const { savedSizesEnabled } = useTemporaryFeatureFlags();
    const { sizePresets: catalogSizePresets } = useSavedSizesCatalog({ enabled: savedSizesEnabled, workspaceId });
    // useSavedSizesCatalog({ enabled: false }) only stops the query from refetching — it keeps serving whatever
    // this query key last cached, including sizes cached by another surface, so the flag must also be checked here.
    const sizePresets = savedSizesEnabled ? catalogSizePresets : NO_SAVED_SIZES;
    const [imageProcessingError, setImageProcessingError] = useState(false);
    const [isExportDisabled, setIsExportDisabled] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    const exportImage = useCallback(
      async (formValues: ExportImagesFormData) => {
        try {
          const { ext, suffix, fileName: initialFileName, size, ...rest } = formValues;
          const fileName = extractFileName(ext, initialFileName, suffix);
          const linkToOpen = createImgixImageUrl({ ...rest, format: ext, fileName });

          if (!linkToOpen) {
            throw new Error('Error exporting image');
          }

          DownloadFile(linkToOpen);

          const isSavedSize = sizePresets.some((preset: SavedSize) => getSavedSizeSelectionId(preset.id) === size);
          const sizeLabel =
            defaultImageSizeOptions.find((sizeOpt) => sizeOpt.value === size)?.label ??
            (isSavedSize ? SAVED_SIZE_ANALYTICS_LABEL : 'Custom');
          trackClickedExportAsButton({ size: sizeLabel, format: ext });
        } catch (error) {
          setImageProcessingError(true);
          reportErrorToBugsnag({ error, context: 'Failed to export image' });
        } finally {
          setIsLoading(false);
          if (imageProcessingError) {
            setImageProcessingError(false);
          }
        }
      },
      [imageProcessingError, sizePresets, trackClickedExportAsButton],
    );

    const onSubmit = useCallback(
      (formValues: ExportImagesFormData) => {
        if (isEditing) {
          return;
        }

        setIsLoading(true);

        // NOTE: exportImage is async because it relies on an iframe loading and some browsers (e.g. Chrome) don't
        // expose iframe.onload so setIsLoading(false) gets called instantly. The timeout allows us to give a better
        // UX by letting the user know briefly that the image is processing.
        setTimeout(() => {
          void exportImage(formValues);
        }, EXPORT_LOADING_DELAY_MS);
      },
      [exportImage, isEditing],
    );

    useEffect(() => {
      stopEscKeyPropagationOnAssetDetailsModal(true);
      return () => {
        stopEscKeyPropagationOnAssetDetailsModal(false);
      };
    }, []);

    const closeModal = useCallback(() => {
      if (isEditing) {
        return;
      }
      onClose();
    }, [isEditing, onClose]);

    const handleCropAndEditOnCanvas = useCallback(() => {
      if (!onCropAndEditOnCanvas) {
        return;
      }

      onCropAndEditOnCanvas();
      onClose();
    }, [onClose, onCropAndEditOnCanvas]);

    return (
      <Modal className="px-8 py-6" isOpen data-testid="EXPORT_IMAGES_MODAL" onDismiss={closeModal}>
        <header className="mb-4 flex flex-col">
          <div className="flex items-center justify-between gap-4">
            <ModalTitle>Export as</ModalTitle>
            <ModalCloseButton className="shrink-0" onClick={onClose} />
          </div>
        </header>
        <Formik<ExportImagesFormData>
          initialValues={{
            width,
            height,
            fileName,
            imageSrc,
            ext: 'jpg',
            size: '1',
          }}
          onSubmit={onSubmit}
          validationSchema={validationSchema}
        >
          <Form id={EXPORT_IMAGES_MODAL_FORM_ID}>
            <ExportImagesModalForm
              imageProcessingError={imageProcessingError}
              setIsExportDisabled={setIsExportDisabled}
              setIsEditing={setIsEditing}
              isEditing={isEditing}
              exportImage={onSubmit}
              sizePresets={sizePresets}
            />
            <div className={cn('mt-4 flex gap-2', onCropAndEditOnCanvas ? 'justify-between' : 'justify-end')}>
              {onCropAndEditOnCanvas ? (
                <Button
                  appearance="outline"
                  color="blue"
                  data-testid="EXPORT_IMAGES_MODAL_CROP_AND_EDIT_ON_CANVAS_BUTTON"
                  disabled={isLoading}
                  onClick={handleCropAndEditOnCanvas}
                  size="large"
                  type="button"
                >
                  Crop and edit on Canvas
                </Button>
              ) : null}
              <div className="flex gap-2">
                <Button
                  data-testid="EXPORT_IMAGES_MODAL_CANCEL_BUTTON"
                  disabled={isLoading}
                  size="large"
                  appearance="ghost"
                  color="gray"
                  onClick={closeModal}
                >
                  Cancel
                </Button>
                <Button
                  data-testid="EXPORT_IMAGES_MODAL_EXPORT_BUTTON"
                  disabled={isExportDisabled || isLoading}
                  isLoading={isLoading}
                  size="large"
                  type="submit"
                  appearance="filled"
                  color="blue"
                >
                  Export
                </Button>
              </div>
            </div>
          </Form>
        </Formik>
      </Modal>
    );
  },
);

ExportImagesModal.displayName = 'ExportImagesModal';
